import { resolveRealmUrl } from "./zulipClient";

/**
 * Zulip's own canonical unicode-emoji naming, fetched from
 * `server_emoji_data_url` (see RegisterResult in chatApi.ts) — a small
 * `{ code_to_names: { [hexCodepoints]: string[] } }` JSON that Zulip's
 * zerver/lib/emoji.py `data_url()` describes as "the more official API for
 * mobile [and other third-party clients] to fetch data about emoji", built
 * at deploy time from whichever emoji dataset the realm's Zulip fork ships
 * (Google's, per the emoji-datasource-google package in this repo's Zulip
 * checkout). Each key is the same dash-joined, zero-padded, unqualified hex
 * codepoint string unicodeEmojiCode() (MessageBubble.tsx) produces; the
 * first element of each names array is always the canonical name.
 *
 * This exists because emoji-picker-react ships its own English names (e.g.
 * "thumbs up") which essentially never match Zulip's internal spelling
 * (e.g. "+1") — and Zulip's check_emoji_request rejects a unicode_emoji
 * reaction whose emoji_name doesn't map back to the exact emoji_code sent,
 * even when the code itself is perfectly valid. Looking the name up here
 * instead of trusting the picker avoids "Invalid emoji name" errors.
 */
let cachedUrl: string | null = null;
let cachedMap: Map<string, string> | null = null;
let inflight: Promise<Map<string, string>> | null = null;

export async function loadZulipEmojiNames(dataUrl: string): Promise<Map<string, string>> {
  if (cachedMap && cachedUrl === dataUrl) return cachedMap;
  if (inflight) return inflight;
  inflight = (async () => {
    const res = await fetch(resolveRealmUrl(dataUrl) || dataUrl);
    if (!res.ok) throw new Error(`Failed to load emoji data (${res.status})`);
    const body: { code_to_names?: Record<string, string[]> } = await res.json();
    const map = new Map<string, string>();
    for (const [code, names] of Object.entries(body.code_to_names ?? {})) {
      if (names?.[0]) map.set(code, names[0]);
    }
    cachedMap = map;
    cachedUrl = dataUrl;
    return map;
  })();
  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}
